import clsx from "clsx";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import HandLeft from "@/assets/friendsWithBenefits/handLeft.svg";
import HandRight from "@/assets/friendsWithBenefits/handRight.svg";
import Logo from "@/assets/header/Logo.svg?react";
import Peace from "@/assets/header/peace.svg";
import SimRed from "@/assets/header/sim-red.svg";
import Sims from "@/assets/header/sims.svg";
import { BurgerDrawer } from "@/components/Layout/BaseLayouts/Sidebar/BurgerDrawer/BurgerDrawer";
import { usePortalShellConfig } from "@/components/Layout/PortalShellConfig";
import { useScrollDirectionContext } from "@/provider/ScrollDirectionProvider/ScrollDirectionProvider";

/**
 * "sims" is the standard pre-login hero, "hands" the Friends with Benefits one.
 */
const heroThemeMapping = {
  sims: {
    banner: "bg-surface-accent-blue",
    headline: "text-foreground-primary-inverse",
  },
  hands: {
    banner: "bg-surface-accent-mint",
    headline: "text-foreground-primary",
  },
};

export type HeroTheme = keyof typeof heroThemeMapping;

interface Props {
  variant: "welcome" | "welcomeLogo" | "portal";
  headline?: string;
  heroTheme?: HeroTheme;
  isMobile?: boolean;
}

const WelcomeLogo = () => (
  <div className="mb-4 mt-8 !h-8 pl-6 desktop:h-20 desktop:pl-8">
    <Logo />
  </div>
);

const PortalLogo = () => {
  const { t } = useTranslation();
  const { homeLink } = usePortalShellConfig();

  if (!homeLink) {
    return <Logo aria-label={t("Common.logoName")} />;
  }

  return (
    <Link to={homeLink} aria-label={t("Common.logoName")}>
      <Logo />
    </Link>
  );
};

export const Header: FC<Props> = ({
  variant,
  headline,
  heroTheme = "sims",
  isMobile,
}) => {
  const { t } = useTranslation();
  const { atTop } = useScrollDirectionContext();
  // Flush full-width bar at rest, floating rounded card once scrolled away
  // from the top -- same toggle as BottomNavBar's atTop-driven state.
  const floating = isMobile && !atTop;

  if (variant === "welcomeLogo") {
    return (
      <div className="flex w-full flex-col bg-background-secondary">
        <WelcomeLogo />
      </div>
    );
  }

  if (variant === "welcome") {
    const heroClasses = heroThemeMapping[heroTheme];

    return (
      <div className="flex w-full flex-col bg-background-secondary">
        <WelcomeLogo />
        <div className="relative px-4 desktop:px-6">
          <div
            className={clsx(
              "flex h-[200px] flex-col justify-center rounded-lg px-4 py-8 desktop:h-[174px]",
              heroClasses.banner,
            )}
          >
            <p
              className={clsx(
                "text-center text-headline-xl font-semibold leading-xs tracking-tight",
                heroClasses.headline,
              )}
            >
              {headline ?? t("Header.welcomeHeadline")}
            </p>
          </div>

          {heroTheme === "sims" ? (
            <>
              <img
                src={SimRed}
                alt="Illustration of Red Sim Card"
                className="pointer-events-none absolute left-0 top-[7.5px] w-[74px] desktop:hidden"
              />
              <img
                src={Sims}
                alt="Illustration of Two Sim Cards"
                className="pointer-events-none absolute -top-4 left-[10px] hidden w-20 desktop:block desktop:w-36"
              />
              <div className="pointer-events-none absolute bottom-0 right-0 hidden h-[112px] w-[124px] overflow-hidden desktop:block">
                <img
                  src={Peace}
                  alt="Illustration of Hand showing Peace Sign"
                  className="h-[124px] w-[124px] object-contain"
                />
              </div>
            </>
          ) : (
            <>
              <img
                className="pointer-events-none absolute left-0 top-4 w-16 desktop:top-6 desktop:w-[251px]"
                src={HandRight}
                alt="Illustration of an open hand"
              />
              <img
                className="pointer-events-none absolute right-0 top-0 hidden desktop:block"
                src={HandLeft}
                alt="Illustration of a pointing hand"
              />
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="header-root"
      className={clsx(
        "flex w-full flex-col transition-transform duration-300 ease-in-out",
        // Card is 56px (h-13 in this project's custom spacing scale where
        // 13=56px/14=64px), 64px total once the 8px floating gap is added.
        isMobile && "fixed left-0 right-0 top-0 z-20",
        floating && "px-2 pt-2",
      )}
    >
      <div
        className={clsx(
          "flex items-center justify-between bg-surface-accent-ivory transition-[border-radius] duration-200 ease-in-out",
          isMobile
            ? "h-13 px-4"
            : "h-20 pl-6 pr-6 pt-6 tablet:pl-8 tablet:pr-8 tablet:pt-8",
          floating && "rounded-lg",
        )}
      >
        <PortalLogo />
        {isMobile && <BurgerDrawer floating={floating} />}
      </div>
    </div>
  );
};
